/**
 * AI Usage Tracking Service
 * Manages usage limits, plan restrictions, and subscription upgrades
 */

import { supabase } from './supabase';

export interface AIUsageLimits {
  can_use: boolean;
  reason: string;
  plan_type: 'free' | 'pro' | 'premium';
  limit: number;
  used: number;
  remaining: number;
  resets_on: string;
}

export interface UserPlan {
  id: string;
  user_id: string;
  plan_type: 'free' | 'pro' | 'premium';
  plan_status: 'active' | 'canceled' | 'suspended' | 'expired';
  started_at: string;
  expires_at?: string;
  next_billing_date?: string;
  billing_cycle: 'monthly' | 'yearly' | 'lifetime';
  
  // AI Usage Limits
  ai_image_scans_limit: number;
  ai_recipe_generation_limit: number;
  ai_meal_analysis_limit: number;
  ai_total_requests_limit: number;
  
  // Special access flags (cleaned up - removed unused feature flags)
  // Note: Other feature flags were removed as they're not used in the app
  
  created_at: string;
  updated_at: string;
}

export interface AIUsageTracking {
  id: string;
  user_id: string;
  usage_month: string;
  image_scans_used: number;
  recipe_generations_used: number;
  meal_analysis_used: number;
  total_ai_requests_used: number;
  estimated_cost_usd: number;
  last_reset_date: string;
  next_reset_date: string;
  created_at: string;
  updated_at: string;
}

export type AIFeatureType = 'image_scan' | 'recipe_generation' | 'meal_analysis' | 'image_generation';

export class AIUsageService {
  
  /**
   * Check if user can use a specific AI feature
   */
  static async checkFeatureLimit(userId: string, featureType: AIFeatureType): Promise<AIUsageLimits> {
    try {
      // Checking AI usage limit
      
      const { data, error } = await supabase.rpc('check_ai_feature_limit', {
        p_user_id: userId,
        p_feature_type: featureType
      });

      if (error) {
        console.error('❌ [AI-USAGE] Error checking feature limit:', error);
        throw error;
      }

      console.log('✅ [AI-USAGE] Feature limit check result:', data);
      return data;
    } catch (error) {
      console.error('💥 [AI-USAGE] Exception checking feature limit:', error);
      throw error;
    }
  }

  /**
   * Increment usage counter for a feature
   */
  static async incrementUsage(
    userId: string,
    featureType: AIFeatureType,
    requestType?: string,
    apiProvider?: string,
    modelUsed?: string,
    tokensUsed?: number,
    processingTimeMs?: number,
    success: boolean = true,
    errorMessage?: string
  ): Promise<boolean> {
    try {
      console.log('📊 [AI-USAGE] Incrementing usage:', { 
        userId: userId.substring(0, 8), 
        featureType, 
        requestType,
        success 
      });
      
      const { data, error } = await supabase.rpc('increment_ai_usage', {
        p_user_id: userId,
        p_feature_type: featureType,
        p_request_type: requestType,
        p_api_provider: apiProvider,
        p_model_used: modelUsed,
        p_tokens_used: tokensUsed,
        p_processing_time_ms: processingTimeMs,
        p_success: success,
        p_error_message: errorMessage
      });

      if (error) {
        console.error('❌ [AI-USAGE] Error incrementing usage:', error);
        return false;
      }

      console.log('✅ [AI-USAGE] Usage incremented successfully');
      return data === true;
    } catch (error) {
      console.error('💥 [AI-USAGE] Exception incrementing usage:', error);
      return false;
    }
  }

  /**
   * Get user's current plan
   */
  static async getUserPlan(userId: string): Promise<UserPlan | null> {
    try {
      // Getting user plan
      
      const { data, error } = await supabase
        .from('user_plans')
        .select('*')
        .eq('user_id', userId)
        .eq('plan_status', 'active')
        .single();

      if (error) {
        // If user plan doesn't exist (PGRST116 = no rows), try to create it
        if (error.code === 'PGRST116') {
          console.warn('⚠️ [AI-USAGE] User plan not found, attempting to create...');
          
          try {
            // Import and use database service to create missing records
            const { databaseService } = await import('./database');
            const result = await databaseService.ensureUserPlan();
            
            if (result.success) {
              console.log('✅ [AI-USAGE] User plan created, retrying...');
              
              // Retry the query once
              const { data: retryData, error: retryError } = await supabase
                .from('user_plans')
                .select('*')
                .eq('user_id', userId)
                .eq('plan_status', 'active')
                .single();
                
              if (retryError) {
                console.error('❌ [AI-USAGE] Still failed after creating user plan:', retryError);
                return null;
              }
              
              console.log('✅ [AI-USAGE] User plan retrieved after creation:', { plan_type: retryData?.plan_type });
              return retryData;
            } else {
              console.error('❌ [AI-USAGE] Failed to create user plan:', result.message);
              return null;
            }
          } catch (createError) {
            console.error('💥 [AI-USAGE] Exception creating user plan:', createError);
            return null;
          }
        } else {
          console.error('❌ [AI-USAGE] Error getting user plan:', error);
          return null;
        }
      }

      console.log('✅ [AI-USAGE] User plan retrieved:', { plan_type: data?.plan_type });
      return data;
    } catch (error) {
      console.error('💥 [AI-USAGE] Exception getting user plan:', error);
      return null;
    }
  }

  /**
   * Get user's current usage for this month
   */
  static async getCurrentUsage(userId: string): Promise<AIUsageTracking | null> {
    try {
      const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM format
      
      console.log('📊 [AI-USAGE] Getting current usage for month:', currentMonth);
      
      const { data, error } = await supabase
        .from('ai_usage_tracking')
        .select('*')
        .eq('user_id', userId)
        .eq('usage_month', currentMonth)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('❌ [AI-USAGE] Error getting current usage:', error);
        return null;
      }

      console.log('✅ [AI-USAGE] Current usage retrieved:', data ? 'found' : 'not found');
      return data;
    } catch (error) {
      console.error('💥 [AI-USAGE] Exception getting current usage:', error);
      return null;
    }
  }

  /**
   * Upgrade user to a new plan
   */
  static async upgradePlan(
    userId: string, 
    newPlanType: 'free' | 'pro' | 'premium',
    billingCycle: 'monthly' | 'yearly' = 'monthly'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('⬆️ [AI-USAGE] Upgrading user plan:', { 
        userId: userId.substring(0, 8), 
        newPlanType, 
        billingCycle 
      });
      
      const { data, error } = await supabase.rpc('upgrade_user_plan', {
        p_user_id: userId,
        p_new_plan_type: newPlanType,
        p_billing_cycle: billingCycle
      });

      if (error) {
        console.error('❌ [AI-USAGE] Error upgrading plan:', error);
        return { success: false, error: error.message };
      }

      console.log('✅ [AI-USAGE] Plan upgraded successfully:', data);
      return data;
    } catch (error) {
      console.error('💥 [AI-USAGE] Exception upgrading plan:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Check if feature is available on user's plan (without usage limits)
   */
  static async isFeatureAvailable(userId: string, feature: string): Promise<boolean> {
    try {
      const plan = await this.getUserPlan(userId);
      if (!plan) return false;

      // All features are available to all users since we removed unused feature flags
      // Only AI usage limits apply (checked via checkFeatureLimit)
      return true;
    } catch (error) {
      console.error('💥 [AI-USAGE] Exception checking feature availability:', error);
      return false;
    }
  }

  /**
   * Get plan pricing information
   */
  static getPlanPricing() {
    return {
      free: {
        name: 'Free',
        price: '$0',
        pricePerMonth: 0,
        features: [
          'Track up to 50 food items',
          '10 recipe generations/month',
          '10 meal logs/month',
          'Manual inventory entry only',
          'Basic meal planner',
          'Basic shopping list'
        ],
        limits: {
          ai_image_scans_limit: 10,
          ai_recipe_generation_limit: 10,
          ai_meal_analysis_limit: 10,
          ai_total_requests_limit: 30
        }
      },
      pro: {
        name: 'Pro',
        price: '$15',
        pricePerMonth: 15,
        features: [
          'Full-featured inventory tracking',
          'Unlimited photo/receipt scanning',
          'AI recipe generation (150/month)',
          'Unlimited meal logs',
          'Full-featured meal planning',
          'Full-featured shopping list',
          'Personalized calorie tracker'
        ],
        limits: {
          ai_image_scans_limit: 200,
          ai_recipe_generation_limit: 150,
          ai_meal_analysis_limit: 150,
          ai_total_requests_limit: 500
        }
      },
      premium: {
        name: 'Premium',
        price: '$19.99',
        pricePerMonth: 19.99,
        features: [
          '500 AI photo scans/month',
          '300 AI recipe generations/month',
          '300 meal analysis/month',
          'Everything in Pro',
          'Data export',
          'Family sharing (up to 6 members)',
          'Custom dietary preferences',
          'Advanced meal planning'
        ],
        limits: {
          ai_image_scans_limit: 500,
          ai_recipe_generation_limit: 300,
          ai_meal_analysis_limit: 300,
          ai_total_requests_limit: 1100
        }
      }
    };
  }

  /**
   * Create a usage middleware function for AI features
   */
  static createUsageMiddleware(featureType: AIFeatureType) {
    return async (userId: string): Promise<{ canProceed: boolean; limits?: AIUsageLimits }> => {
      try {
        const limits = await this.checkFeatureLimit(userId, featureType);
        
        if (!limits.can_use) {
          console.warn('🚫 [AI-USAGE] Feature blocked by limits:', {
            featureType,
            reason: limits.reason,
            used: limits.used,
            limit: limits.limit
          });
        }
        
        return {
          canProceed: limits.can_use,
          limits
        };
      } catch (error) {
        console.error('💥 [AI-USAGE] Middleware error:', error);
        return { canProceed: false };
      }
    };
  }

  /**
   * Format usage limits for display
   */
  static formatUsageLimits(limits: AIUsageLimits): string {
    const resetDate = new Date(limits.resets_on).toLocaleDateString();
    
    if (!limits.can_use) {
      return `${limits.reason} Resets on ${resetDate}`;
    }
    
    return `${limits.remaining} of ${limits.limit} remaining this month`;
  }

  /**
   * Get upgrade recommendation based on usage
   */
  static getUpgradeRecommendation(limits: AIUsageLimits | null): {
    shouldUpgrade: boolean;
    message: string;
    recommendedPlan: 'pro' | 'premium';
  } {
    // Handle null/undefined limits
    if (!limits) {
      return {
        shouldUpgrade: false,
        message: 'Unable to check usage limits. Please try again.',
        recommendedPlan: 'pro'
      };
    }

    if (limits.plan_type === 'free' && !limits.can_use) {
      return {
        shouldUpgrade: true,
        message: 'Upgrade to Pro for 200 AI requests per month!',
        recommendedPlan: 'pro'
      };
    }
    
    if (limits.plan_type === 'pro' && limits.remaining < 10) {
      return {
        shouldUpgrade: true,
        message: 'Upgrade to Premium for 1000 AI requests per month!',
        recommendedPlan: 'premium'
      };
    }
    
    return {
      shouldUpgrade: false,
      message: `You're all set! ${limits.remaining} requests remaining.`,
      recommendedPlan: 'pro'
    };
  }

  // ==================== ADMIN FUNCTIONS ====================

  /**
   * Toggle AI usage limits globally (ADMIN ONLY)
   */
  static async adminToggleLimits(
    adminUserId: string, 
    limitsEnabled: boolean, 
    adminNotes?: string
  ): Promise<{ success: boolean; message: string; error?: string }> {
    try {
      console.log('🔧 [AI-ADMIN] Toggling limits:', { 
        adminUserId: adminUserId.substring(0, 8), 
        limitsEnabled, 
        adminNotes 
      });
      
      const { data, error } = await supabase.rpc('admin_toggle_ai_limits', {
        p_admin_user_id: adminUserId,
        p_limits_enabled: limitsEnabled,
        p_admin_notes: adminNotes
      });

      if (error) {
        console.error('❌ [AI-ADMIN] Error toggling limits:', error);
        return { success: false, message: 'Failed to toggle limits', error: error.message };
      }

      if (!data.success) {
        return { success: false, message: data.error };
      }

      console.log('✅ [AI-ADMIN] Limits toggled successfully:', data.message);
      return { success: true, message: data.message };
    } catch (error) {
      console.error('💥 [AI-ADMIN] Exception toggling limits:', error);
      return { success: false, message: 'Exception occurred', error: String(error) };
    }
  }

  /**
   * Get AI usage analytics (ADMIN ONLY)
   */
  static async adminGetAnalytics(
    adminUserId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<{ success: boolean; analytics?: any; error?: string }> {
    try {
      console.log('📊 [AI-ADMIN] Getting analytics:', { 
        adminUserId: adminUserId.substring(0, 8), 
        startDate, 
        endDate 
      });
      
      const { data, error } = await supabase.rpc('admin_get_usage_analytics', {
        p_admin_user_id: adminUserId,
        p_start_date: startDate?.toISOString().split('T')[0],
        p_end_date: endDate?.toISOString().split('T')[0]
      });

      if (error) {
        console.error('❌ [AI-ADMIN] Error getting analytics:', error);
        return { success: false, error: error.message };
      }

      if (!data.success) {
        return { success: false, error: data.error };
      }

      console.log('✅ [AI-ADMIN] Analytics retrieved successfully');
      return { success: true, analytics: data.analytics };
    } catch (error) {
      console.error('💥 [AI-ADMIN] Exception getting analytics:', error);
      return { success: false, error: String(error) };
    }
  }

  /**
   * Grant VIP/Tester access to user (ADMIN ONLY)
   */
  static async adminGrantVIPAccess(
    adminUserId: string,
    targetUserId: string,
    isTester: boolean = true
  ): Promise<{ success: boolean; message: string; error?: string }> {
    try {
      console.log('👑 [AI-ADMIN] Granting VIP access:', { 
        adminUserId: adminUserId.substring(0, 8), 
        targetUserId: targetUserId.substring(0, 8),
        isTester
      });
      
      // First check admin permissions by trying to get analytics (will fail if not admin)
      const adminCheck = await this.adminGetAnalytics(adminUserId);

      if (!adminCheck.success) {
        return { success: false, message: 'Unauthorized: Admin access required' };
      }

      // Grant VIP access
      const { error } = await supabase
        .from('user_plans')
        .upsert({
          user_id: targetUserId,
          plan_type: 'free', // Keep as free but with unlimited access
          unlimited_access: true,
          is_tester: isTester,
          plan_status: 'active'
        });

      if (error) {
        console.error('❌ [AI-ADMIN] Error granting VIP access:', error);
        return { success: false, message: 'Failed to grant VIP access', error: error.message };
      }

      console.log('✅ [AI-ADMIN] VIP access granted successfully');
      return { 
        success: true, 
        message: `VIP access granted to user. They now have unlimited AI usage${isTester ? ' (marked as tester)' : ''}.` 
      };
    } catch (error) {
      console.error('💥 [AI-ADMIN] Exception granting VIP access:', error);
      return { success: false, message: 'Exception occurred', error: String(error) };
    }
  }

  /**
   * Get current global settings (PUBLIC - for frontend toggle display)
   */
  static async getGlobalSettings(): Promise<{
    success: boolean;
    settings?: {
      limits_enabled: boolean;
      emergency_unlimited_mode: boolean;
      maintenance_mode: boolean;
      admin_notes: string;
    };
    error?: string;
  }> {
    try {
      const { data, error } = await supabase
        .from('ai_usage_settings')
        .select('limits_enabled, emergency_unlimited_mode, maintenance_mode, admin_notes')
        .limit(1)
        .single();

      if (error) {
        console.error('❌ [AI-ADMIN] Error getting global settings:', error);
        return { success: false, error: error.message };
      }

      return { success: true, settings: data };
    } catch (error) {
      console.error('💥 [AI-ADMIN] Exception getting global settings:', error);
      return { success: false, error: String(error) };
    }
  }
}
